/**
 * A utility method that will return the object presented or throw an exception if it
 * is not defined.
 */
export function requireObject<T>(obj: T, errMsg = "Object can not be undefined."): T {
    if (!obj) {
        throw new Error(errMsg);
    }
    return obj;
}

/**
 * A utility method that will return an object that contains items which are different between the two
 * items in equality.
 *
 * The values of the items will be obtains from obj2.
 *
 * Example:
 *
 * const obj1 = {
 *   param1: "value",
 *   param2: "value",
 *   param3: "value"
 *   object1: {
 *      param1: "value"
 *   },
 *   object2: {
 *      param1: "value"
 *   },
 *   arr1: [{ param1: "value" }, { param2: "value" }],
 *   arr2: [{ param1: "value" }, { param2: "value" }]
 * }
 * const obj2 = {
 *   param1: "value"
 *   param2: "Different",
 *   param3: "value",
 *   param4: "value",
 *   object1: {
 *      param1: "value"
 *   },
 *   object2: {
 *      param1: "Different"
 *   },
 *   arr1: [{ param1: "value" }, { param2: "value" }],
 *   arr2: [{ param1: "value" }, { param2: "Different" }]
 * }
 *
 * const obj3 = diff(obj1, obj2);
 * assert(obj3).to.deep.equal({
 *    param2: "Different",
 *    object2: {
 *       param1: "Different"
 *    },
 *    arr2: [{ param1: "value" }, { param2: "Different" }]
 * })
 *
 * @export
 * @template T
 * @template U
 * @param {T} obj The first item to check
 * @param {U} obj2 The second item to check.
 * @param deep If true, it will traverse the objects to check for equality.
 * @return A union of the two objects in which only the un-equal items are checked.
 */
export function diff<T>(obj: T, obj2: T): Partial<T> {
    // Add a couple of pre-checks incase we have undefined/null
    if (!obj) {
        if (!obj2) {
            // both are falsey
            // NOTE: potentially could both be undefined here but we
            // are just passing null
            /* tslint:disable:no-null-keyword */
            return null;
            /* tslint:enable:no-null-keyword */
        } else {
            return obj2;
        }
    }

    // no obj2
    if (!obj2) {
        return obj;
    }

    const item: Partial<T> = {};
    const oneKeys = Object.keys(obj) as (keyof T)[];
    for (const key of oneKeys) {
        const value1 = obj[key];
        const value2 = obj2[key];
        if (typeof value1 === "object") {
            if (typeof value2 === "object") {
                const difference = diff(value1, value2);
                if (difference && Object.keys(difference).length > 0) {
                    item[key] = value2;
                }
            } else {
                // Types are different
                item[key] = value2;
            }
        } else if (Array.isArray(value1)) {
            const diffArray: any[] = [];
            if (Array.isArray(value2)) {
                const arr1 = value1 as Array<any>;
                const arr2 = value2 as Array<any>;
                for (let i = 0; i < arr1.length; ++i) {
                    if (typeof arr1[i] === "object") {
                        if (typeof arr2[i] === "object") {
                            if (Object.keys(diff(arr1[i], arr1[i])).length > 0) {
                                diffArray.push(arr2[i]);
                            }
                        } else {
                            diffArray.push(arr2[i]);
                        }
                    }
                }
            }
            if (diffArray.length !== 0) {
                item[key] = value2;
            }
        } else if (value1 !== value2) {
            item[key] = value2;
        }
    }

    // Adding whatever object are left in the second object.
    const twoKeys = Object.keys(obj2) as (keyof T)[];
    const diffKeys = twoKeys.filter(k => !oneKeys.includes(k));
    for (const key of diffKeys) {
        item[key] = obj2[key];
    }
    return item;
}

/**
 * This fixes short-commings in the standard javascript method in which
 *
 * ```
 * const merge = { ...defaultObj, ...obj };
 * ```
 *
 * In the example above, an object will not be overridden with the default if it is defined as 'undefined' in "obj".
 *
 * For example:
 *
 * ```
 * const merge = { ...{ value: "Default Value", ...{ value: undefined }}};
 * ```
 *
 * This will result in `merge === { value: undefined };` when the intended result is `merge === { value: "Default Value" };`
 *
 * This will merge the full objects with the default values if they are defined as `undefined` in "obj".
 * @param obj The main object.
 * @param defaultObj Default values.
 */
export function mergeDefaults(defaultObj: any, obj: any) {
    if (!obj) {
        return defaultObj;
    }

    const merged = { ...defaultObj, ...obj };
    const keys = Object.keys(merged);
    keys.forEach((key: string) => {
        if (!(<any>obj)[key]) {
            merged[key] = (<any>defaultObj)[key];
        }
    });
    return merged;
}

/**
 * A convenience function that allows you to retrieve the value of an object if it is a string or
 * if it is a property in the object.
 *
 * For example,
 * A User ID can be like this: "string | { user_id: string } | { userId: string }"
 *
 * You would retrieve the value with this method like so:
 * const userId = getString(id, ["user_id", "userId"]);
 *
 * @param item The item to retrieve the value from.  Can be a string or object.
 * @param options  The values that the object has that has the property you want.
 */
export function getString(item: string | any, options: string | string[]): string {
    return getType(item, options, isString);
}

/**
 * The engine that drives the "get<parameterType>" functions.  This will
 * query through a given item looking for a specific value.  This doesn't have
 * any type guards as it can return anything.
 * @param item The item that is either the type or is contained within the item.
 * @param options The item key or keys that are to be searched if the passed in item is an object.
 * @param itType - A function that checks if the passed in item is already of a specific type.
 */
export function getType(item: any, options: string | string[], isType: (item: any) => boolean): any {
    if (!item) {
        return undefined;
    }
    if (isType(item)) {
        return item;
    }

    const opts = [].concat(options);
    const found = opts.find((value: string) => {
        return item[value] !== undefined;
    });
    return found ? item[found] : undefined;
}

function isString(item: any) {
    return typeof item === "string";
}

/**
 * Combines two arrays together in which they are unique.
 * @param arr1 First array to merge
 * @param arr2 Second array to merge
 * @param key The key with which to base the uniqueness of the object.
 */
export function union<T, TKey extends keyof T>(arr1: T | T[], arr2: T | T[], key: TKey): T[] {
    const firstArr = [].concat(arr1 || []);
    const secondArr = [].concat(arr2 || []);
    const returnArr = firstArr.concat(secondArr);
    const assoc: any = {};

    return returnArr.filter((obj: any) => {
        const val = obj[key];
        const doesNotContain = !assoc[val];
        if (doesNotContain) {
            assoc[val] = true;
        }
        return doesNotContain;
    });
}

/**
 * Replaces the first instance of the object in the array with the current object.  This is a shallow copy
 * so its reference will be the same through all.
 * @param arr1 The array to replace everything.  If undefined, the item will just be added to it.
 * @param obj The object to replace. If undefined, nothing will happen.
 * @param key The key in which to determine the object uniqueness.
 * @param addIfNotFound If the object was not found in the list, then it will be appended.  If this is set and true,
 * then passing in an empty array will create a new array with the object inside.
 */
export function replace<T>(arr1: T[], obj: T, key: string, addIfNotFound?: boolean): T[] {
    if (!arr1 || arr1.length === 0) {
        return addIfNotFound ? [obj] : [];
    }
    if (!obj) {
        return arr1;
    }
    const firstArr = [].concat(arr1 || []);
    const returnArr = firstArr.slice();
    const objVal = (obj as any)[key];
    const length = firstArr.length;

    let notFound = true;
    for (let i = 0; i < length; ++i) {
        const itemVal = firstArr[i][key];
        if (objVal === itemVal) {
            returnArr[i] = obj;
            notFound = false;
            break;
        }
    }

    if (addIfNotFound && notFound) {
        returnArr.push(obj);
    }

    return returnArr;
}

/**
 * Removes the element from an array based on the check.
 *
 * @export
 * @template T
 * @param {T[]} arr1 The array to remove the element from.
 * @param {(check: T) => boolean} obj A function that checks if this is the object to remove.
 * @returns {T} The object that was removed.
 */
export function remove<T>(arr1: T[], checkFunc: (check: T) => boolean): T {
    const index = arr1.findIndex(checkFunc);
    const obj = index >= 0 ? arr1.splice(index, 1)[0] : undefined;
    return obj;
}

/**
 * Automatically binds all the methods of a class to the object.  Ideally, would be placed inside
 * the constructor of a class like so:
 *
 * class Obj1 {
 *    constructor() {
 *       autoBind(this);
 *    }
 * }
 * @param classObj
 */
export function autoBind(classObj: any) {
    const keys = Object.getOwnPropertyNames(classObj.constructor.prototype);
    keys.forEach(key => {
        const val = classObj[key];
        if (key !== "constructor" && typeof val === "function") {
            classObj[key] = val.bind(classObj);
        }
    });
}

/**
 * A function that removes duplicate items from the array.
 */
export type RemoveDupes<T> = (items: T[]) => T[];

/**
 * Removes duplicate items from an array of items.  The original array is untouched.
 *
 * @export
 * @template T
 * @param {(item: T) => string} getKey A function that returns the unique identifying key of the item.
 * @param {T[]} items The list of items to filter.
 * @returns {T[]} The filtered array that removed duplicate items.
 */
export function removeDuplicates<T>(getKey: (item: T) => string, items: T[] = []): T[] {
    const foundItems: { [key: string]: boolean } = {};
    const copy: T[] = [];
    for (const item of items) {
        const key = getKey(item);
        if (!foundItems[key]) {
            copy.push(item);
            foundItems[key] = true;
        }
    }
    return copy;
}

const getStringKey = (item: string) => item;

export const removeDupeStrings: RemoveDupes<string> = removeDuplicates.bind(undefined, getStringKey);

export function openExternalURL(url: string) {
    const newWindow = window.open();
    // prevents potential foul play https://dev.to/ben/the-targetblank-vulnerability-by-example
    newWindow.opener = undefined;
    newWindow.location.assign(url);
}
